// File for logic corresponding to weather-timeline component
import { Component } from 'js-component-framework';
import Debounce from 'utils/Debounce';
import 'slick-carousel';
import 'slick-carousel/slick/slick.scss';
import 'slick-carousel/slick/slick-theme.scss';
import './index.scss';

/**
 * Component for the weather timeline module.
 */
export default class WeatherTimeline extends Component {
  /**
   * Start the component
   */
  constructor(config) {
    super(config);

    this.slidesToShow = this.element.dataset.slidesToShow;
    this.shouldUpdateLayout = this.shouldUpdateLayout.bind(this);
    this.setTimelineItemOnEvents = this.setTimelineItemOnEvents.bind(this);
    this.timelineItemOnClick = this.timelineItemOnClick.bind(this);
    // eslint-disable-next-line max-len
    this.detailPanelCloseButtonOnClick = this.detailPanelCloseButtonOnClick.bind(this);
    this.onResizeDebounce = new Debounce(
      this.shouldUpdateLayout,
      'resize',
      window,
      250,
      false,
    );

    // Add js-enabled class for style adjustment.
    this.children.timelineContent.classList.add('js-enabled');

    // Set up timeline item click handler.
    if (this.children.timelineItems) {
      Array.prototype.forEach.call(this.children.timelineItems, (item) => {
        this.setTimelineItemOnEvents(item);
      });
    }

    // Set up detail panel close button click handler.
    if (this.children.detailPanelCloseButton) {
      this.children.detailPanelCloseButton.addEventListener(
        'click',
        this.detailPanelCloseButtonOnClick,
      );
    }

    // Fire off layout update.
    this.shouldUpdateLayout();
    // Trigger 'resize' event to correctly calculate `scrollWidth`.
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * Set timeline item event handlers. We need to test for when "click and drag"
   * is occuring. If interation is occuring with the carousel, we do not want
   * to fire click functions for the timeline item.
   *
   * @param {element} item timeline item.
   */
  setTimelineItemOnEvents(item) {
    let isDragging = false;
    item.addEventListener('mousedown', () => { isDragging = false; });
    item.addEventListener('mousemove', () => { isDragging = true; });
    item.addEventListener('mouseup', (event) => {
      if (isDragging) {
        return;
      }

      this.timelineItemOnClick(event);
    });
    item.addEventListener('click', (event) => { event.preventDefault(); });
  }

  /**
   * Determine if we should display timeline as a carousel.
   */
  shouldUpdateLayout() {
    const { timelineContent } = this.children;
    const { scrollWidth, clientWidth } = timelineContent;

    const slidesToShow = parseInt(this.slidesToShow, 10) || 4;
    const slidesToScroll = parseInt(this.slidesToShow, 10) || 4;
    /* eslint-disable no-dupe-keys */
    Object.assign(
      this.options.slickOptions, {
        slidesToShow: 4,
        slidesToScroll: 4,
        responsive: [
          {
            breakpoint: 900,
            settings: {
              slidesToShow,
              slidesToScroll,
            },
          },
        ],
      },
    );

    if (scrollWidth > clientWidth
      && ! timelineContent.classList.contains('slick-initialized')) {
      $(timelineContent).slick(this.options.slickOptions);
    }
  }

  /**
   * Stuff to do upon clicking a timeline item.
   *
   */
  timelineItemOnClick(event) {
    const {
      detailPanel,
      detailPanelDate,
      detailPanelDayTime,
      detailPanelNightTime,
      detailPanelUvIndex,
      detailPanelHumidity,
    } = this.children;

    const {
      date,
      dayTimeNarrative,
      nightTimeNarrative,
      uvIndex,
      humidity,
    } = event.currentTarget.dataset;

    detailPanel.setAttribute('aria-hidden', false);

    if (detailPanelDate && date) {
      detailPanelDate.innerText = date;
    }

    if (detailPanelDayTime && dayTimeNarrative) {
      detailPanelDayTime.innerText = dayTimeNarrative;
    }

    if (detailPanelNightTime && nightTimeNarrative) {
      detailPanelNightTime.innerText = nightTimeNarrative;
    }

    if (detailPanelUvIndex && uvIndex) {
      detailPanelUvIndex.innerText = uvIndex;
    }

    if (detailPanelHumidity && humidity) {
      detailPanelHumidity.innerText = humidity;
    }
  }

  /**
   * Stuff to do upon clicking the detail panel close button.
   */
  detailPanelCloseButtonOnClick() {
    const { detailPanel } = this.children;
    detailPanel.setAttribute('aria-hidden', true);
  }
}
